import { supabase } from './supabase';
import {
  DEFAULT_CHECK_VALVE_DATA,
  checkValveBlock,
  computeCheckValveMetrics,
  finiteOrNull,
  inferCheckValveProfile,
  mergeCheckValveData,
  validateCheckValveRows,
} from './check-valve-calibration-profile';
import {
  DEFAULT_VALVE_VALIDITY_MONTHS,
  addValveValidityDaysToIsoDate,
  configureSafetyValveStandard,
  listSafetyValveStandardOptions,
  validateSafetyValveStandard,
} from './api-safety-valve-calibration';

export { DEFAULT_VALVE_VALIDITY_MONTHS, addValveValidityDaysToIsoDate };
export const listCheckValveStandardOptions = listSafetyValveStandardOptions;
export const validateCheckValveStandard = validateSafetyValveStandard;
export const configureCheckValveStandard = configureSafetyValveStandard;

async function currentUserId() { return (await supabase.auth.getUser())?.data?.user?.id ?? null; }
function sanitizePatch(patch = {}) { return Object.fromEntries(Object.entries(patch).filter(([, value]) => value !== undefined && !(typeof value === 'number' && !Number.isFinite(value)))); }
async function getTodayEnvironment() {
  const { getTodayDailyEnvironmentLog } = await import('./api-pressure-manometer-pilot');
  try { return await getTodayDailyEnvironmentLog(); } catch { return null; }
}

export async function createCheckValveSession({ instrumentId, serviceOrderId = null, serviceOrderCode = null, certificateType = 'NO_SAC' }) {
  if (!instrumentId) throw new Error('instrumentId es requerido');
  const { data: instrument, error: instrumentError } = await supabase
    .from('instruments')
    .select(`id, tag, lab_id, brand, model, serial, type, unit, resolution, range_min, range_max, tolerance, calibration_place,
             category:category_id ( name ), client:client_id ( id, client_number, name, address, locality, province )`)
    .eq('id', instrumentId).single();
  if (instrumentError) throw instrumentError;
  const profile = inferCheckValveProfile(instrument);
  const environment = await getTodayEnvironment();
  const familyData = mergeCheckValveData({
    ...DEFAULT_CHECK_VALVE_DATA,
    pressure_unit: profile.unit,
    test_pressure: finiteOrNull(instrument.range_max),
    environment_readings: DEFAULT_CHECK_VALVE_DATA.environment_readings.map((row) => ({
      ...row, temp_c: finiteOrNull(environment?.temp_c), pressure_hpa: finiteOrNull(environment?.pressure_hpa), humidity_rh: finiteOrNull(environment?.humidity_rh),
    })),
  });
  const calibrationDate = new Date().toISOString().slice(0, 10);
  const { data: session, error } = await supabase.from('calibration_sessions').insert({
    family: 'valve', instrument_id: instrument.id, service_order_id: serviceOrderId, service_order_code: serviceOrderCode || null,
    certificate_type: certificateType, status: 'draft', template_code: profile.code, template_revision: profile.revision,
    client_number: instrument?.client?.client_number ? String(instrument.client.client_number) : null,
    client_name: instrument?.client?.name || null, client_address: instrument?.client?.address || null,
    client_city: instrument?.client?.locality || null, client_province: instrument?.client?.province || null,
    instrument_object: instrument?.category?.name || instrument?.type || profile.title,
    instrument_brand: instrument?.brand || null, instrument_model: instrument?.model || null,
    instrument_serial: instrument?.serial || null, instrument_tag: instrument?.tag || null,
    instrument_unit: profile.unit, instrument_resolution: finiteOrNull(instrument?.resolution),
    instrument_range_min: finiteOrNull(instrument?.range_min), instrument_range_max: finiteOrNull(instrument?.range_max),
    instrument_class: finiteOrNull(instrument?.tolerance), instrument_location: instrument?.calibration_place || 'Laboratorio',
    ambient_pressure_avg: finiteOrNull(environment?.pressure_hpa), ambient_temp_avg: finiteOrNull(environment?.temp_c), ambient_humidity_avg: finiteOrNull(environment?.humidity_rh),
    calibration_date: calibrationDate, validity_months: DEFAULT_VALVE_VALIDITY_MONTHS,
    valid_until: addValveValidityDaysToIsoDate(calibrationDate, DEFAULT_VALVE_VALIDITY_MONTHS), family_data: familyData, created_by: await currentUserId(),
  }).select('*').single();
  if (error) throw error;
  const { error: blockError } = await supabase.from('calibration_session_blocks').insert({ ...checkValveBlock(), session_id: session.id });
  if (blockError) throw blockError;
  return session;
}

export async function getCheckValveSession(sessionId) {
  const { data, error } = await supabase.from('calibration_sessions').select('*').eq('id', sessionId).eq('family', 'valve').eq('template_code', 'VALVE:check_nonreturn:v1').single();
  if (error) throw error;
  return data;
}

export async function updateCheckValveSession(sessionId, patch) {
  const normalized = sanitizePatch(patch);
  if (normalized.family_data) normalized.family_data = mergeCheckValveData(normalized.family_data);
  const { data, error } = await supabase.from('calibration_sessions').update({ ...normalized, updated_at: new Date().toISOString(), updated_by: await currentUserId() })
    .eq('id', sessionId).eq('family', 'valve').eq('template_code', 'VALVE:check_nonreturn:v1').select('*').single();
  if (error) throw error;
  return data;
}

export async function listCheckValveSessionsByInstrument(instrumentId) {
  const { data, error } = await supabase.from('calibration_sessions').select('*').eq('instrument_id', instrumentId).eq('family', 'valve')
    .eq('template_code', 'VALVE:check_nonreturn:v1').order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getCheckValveBlock(sessionId) {
  const { data, error } = await supabase.from('calibration_session_blocks').select('*').eq('session_id', sessionId).eq('block_key', 'check_valve_functional').single();
  if (error) throw error;
  return data;
}
export async function listCheckValvePoints(sessionId) {
  const { data, error } = await supabase.from('calibration_session_points').select('*').eq('session_id', sessionId).order('series_no').order('point_no');
  if (error) throw error;
  return data || [];
}
export async function listCheckValveResults(sessionId) {
  const { data, error } = await supabase.from('calibration_session_results').select('*').eq('session_id', sessionId).order('point_no');
  if (error) throw error;
  return data || [];
}

export function checkValvePointsToRows(points = [], session = {}) {
  const familyData = mergeCheckValveData(session.family_data);
  return Array.from({ length: familyData.repeat_count }, (_, index) => {
    const trial = index + 1;
    const applied = points.find((point) => Number(point.series_no) === trial && point.direction === 'REVERSE_TEST');
    const leak = points.find((point) => Number(point.series_no) === trial && point.direction === 'LEAK_ONSET');
    return {
      trial_no: trial,
      applied_pressure: applied?.pattern_reading ?? '',
      reverse_result: applied?.instrument_reading_raw || 'not_performed',
      leak_pressure: leak?.pattern_reading ?? '',
    };
  });
}

export async function saveCheckValvePoints(sessionId, block, rows = [], familyData = {}) {
  const merged = mergeCheckValveData(familyData);
  const payload = [];
  rows.slice(0, merged.repeat_count).forEach((row, index) => {
    const series = index + 1;
    const applied = finiteOrNull(row.applied_pressure);
    const leak = finiteOrNull(row.leak_pressure);
    if (applied !== null) payload.push({
      session_id: sessionId, block_id: block.id, point_no: 1, series_no: series, direction: 'REVERSE_TEST',
      setpoint: merged.test_pressure, pattern_reading: applied, instrument_reading: merged.test_pressure,
      instrument_reading_raw: row.reverse_result || 'not_performed', unit: merged.pressure_unit,
    });
    if (leak !== null) payload.push({
      session_id: sessionId, block_id: block.id, point_no: 2, series_no: series, direction: 'LEAK_ONSET',
      setpoint: merged.test_pressure, pattern_reading: leak, instrument_reading_raw: 'fail', unit: merged.pressure_unit,
    });
  });
  const { error: resultDeleteError } = await supabase.from('calibration_session_results').delete().eq('session_id', sessionId);
  if (resultDeleteError) throw resultDeleteError;
  const { error: deleteError } = await supabase.from('calibration_session_points').delete().eq('session_id', sessionId);
  if (deleteError) throw deleteError;
  if (!payload.length) return [];
  const { data, error } = await supabase.from('calibration_session_points').insert(payload).select('*');
  if (error) throw error;
  return data || [];
}

export async function calculateCheckValveResults(sessionId) {
  const validation = await validateCheckValveStandard(sessionId);
  if (!validation?.valid) throw new Error((validation?.issues || ['La trazabilidad no es válida.']).join(' '));
  const [session, block, points] = await Promise.all([getCheckValveSession(sessionId), getCheckValveBlock(sessionId), listCheckValvePoints(sessionId)]);
  const snapshot = validation.configuration?.reference || null;
  const familyData = mergeCheckValveData({ ...session.family_data, standard_configuration: { reference: snapshot } });
  const rows = checkValvePointsToRows(points, { ...session, family_data: familyData });
  const issues = validateCheckValveRows(rows, familyData, snapshot);
  if (issues.length) throw new Error(issues.join(' '));
  const metrics = computeCheckValveMetrics(rows, { ...session, family_data: familyData }, snapshot, block);
  if (!metrics) throw new Error('No fue posible calcular los resultados de la válvula antirretorno.');
  const results = [{
    session_id: sessionId, block_id: block.id, point_no: 1,
    pattern_mean: metrics.target_pressure, instrument_mean: metrics.applied.mean,
    error_abs: metrics.applied.mean - metrics.target_pressure,
    error_pct: ((metrics.applied.mean - metrics.target_pressure) / metrics.target_pressure) * 100,
    hysteresis: null, repeatability: metrics.applied.repeatability,
    u_standard: metrics.applied.u_standard, u_drift: 0, u_resolution: metrics.applied.u_resolution,
    u_repeatability: metrics.applied.u_repeatability, u_temp: 0,
    u_other: Math.hypot(metrics.applied.u_hysteresis, metrics.applied.u_other),
    uc: metrics.applied.uc, k: metrics.applied.k, u_expanded: metrics.applied.u_expanded,
    u_reported: metrics.applied.u_expanded, in_tolerance: metrics.pressure_pass,
    uncertainty_components: {
      standard: metrics.applied.u_standard, resolution: metrics.applied.u_resolution,
      repeatability: metrics.applied.u_repeatability, hysteresis: metrics.applied.u_hysteresis,
      other: metrics.applied.u_other, minimum_corrected_pressure: metrics.applied.minimum,
      decision_rule: metrics.decision_rule,
    },
  }, {
    session_id: sessionId, block_id: block.id, point_no: 2,
    pattern_mean: null, instrument_mean: null, error_abs: null, error_pct: null,
    hysteresis: null, repeatability: null, u_standard: null, u_drift: 0, u_resolution: null,
    u_repeatability: null, u_temp: 0, u_other: null, uc: null, k: 2, u_expanded: null,
    u_reported: null, in_tolerance: metrics.forward_pass && metrics.reverse_pass,
    uncertainty_components: { forward_pass: metrics.forward_pass, reverse_pass: metrics.reverse_pass, failed_trials: metrics.failed_trials },
  }];
  const { error: deleteError } = await supabase.from('calibration_session_results').delete().eq('session_id', sessionId);
  if (deleteError) throw deleteError;
  const { error } = await supabase.from('calibration_session_results').insert(results);
  if (error) throw error;
  await updateCheckValveSession(sessionId, { status: 'calculated', instrument_unit: familyData.pressure_unit, family_data: mergeCheckValveData({ ...familyData, result_summary: metrics }) });
  return results;
}

export function targetCheckPressureBar(familyData = {}) { return mergeCheckValveData(familyData).target_pressure_bar; }
